
import { supabase } from "@/integrations/supabase/client";
import type { CalendarEventsResponse } from "./types";

// Enhanced cache with month-based keys for better hit rates
const availabilityCache = new Map();
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes - longer cache for better performance

// Request deduplication to prevent infinite loops
const pendingRequests = new Map<string, Promise<CalendarEventsResponse>>();

/**
 * Generate cache key based on month range instead of exact dates
 */
function generateCacheKey(hostUserId: string, start: string, end: string, timezone?: string, excludeEventId?: string): string {
  const startDate = new Date(start);
  const endDate = new Date(end);
  
  // Create month-based cache key for better cache hits
  const startMonth = `${startDate.getFullYear()}-${String(startDate.getMonth() + 1).padStart(2, '0')}`;
  const endMonth = `${endDate.getFullYear()}-${String(endDate.getMonth() + 1).padStart(2, '0')}`;
  
  return `host_${hostUserId}_${startMonth}_${endMonth}_${timezone || 'UTC'}_${excludeEventId || 'none'}`;
}

/**
 * Fetch host availability with optimized batch requests and caching
 */
export async function fetchHostAvailability(
  hostUserId: string,
  start: string,
  end: string,
  timezone?: string,
  excludeEventId?: string // NEW: exclude specific calendar event (for reschedule mode)
): Promise<CalendarEventsResponse> {
  // Debug mode handling
  if (hostUserId === 'debug') {
    console.log("Debug mode: returning empty events list for host availability");
    return { items: [] };
  }
  
  try {
    // Validate required parameters
    if (!start || !end) {
      console.error('[fetchHostAvailability] Missing required parameters:', { hostUserId, start, end });
      return {
        items: [],
        error: "Start time and end time are required for calendar availability check"
      };
    }

    // Check cache first
    const cacheKey = generateCacheKey(hostUserId, start, end, timezone, excludeEventId);
    const cached = availabilityCache.get(cacheKey);
    
    if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
      console.log(`[fetchHostAvailability] Cache hit for ${cacheKey}`);
      return { items: cached.data };
    }

    // Check if there's already a pending request for this key
    if (pendingRequests.has(cacheKey)) {
      console.log(`[fetchHostAvailability] Deduplicating request for ${cacheKey}`);
      return await pendingRequests.get(cacheKey)!;
    }

    console.log(`[fetchHostAvailability] Fetching host availability for ${hostUserId} from ${start} to ${end} with timezone: ${timezone || 'default'}`);
    
    // Create the request promise and store it for deduplication
    const requestPromise = (async () => {
      try {
        // Use consistent parameter names that match the edge function expectations
        const body: any = { 
          action: "get_host_events", 
          hostId: hostUserId,
          startTime: start, 
          endTime: end 
        };
        
        // Add timezone if provided
        if (timezone) {
          body.timezone = timezone;
        }
        
        console.log('[fetchHostAvailability] Request body:', JSON.stringify(body, null, 2));
        
        const { data, error } = await supabase.functions.invoke("google-calendar", { body });

        if (error) {
          console.error("Error fetching host availability:", error);
          return { items: [], error: error.message || "Failed to fetch host availability" };
        }

        const result = data || { items: [] };
        
        if (result.error) {
          console.error("API error in host availability:", result.error);
          return { items: [], error: result.error };
        }

        // Filter out the excluded event if provided (for reschedule mode)
        let events = result.items || [];
        if (excludeEventId) {
          const originalCount = events.length;
          events = events.filter((event: any) => event.id !== excludeEventId);
          console.log(`[fetchHostAvailability] Excluded event ${excludeEventId} from host availability (${originalCount} -> ${events.length})`);
        }
        
        // Cache the successful response (with filtered events)
        availabilityCache.set(cacheKey, {
          data: events,
          timestamp: Date.now()
        });

        console.log(`[fetchHostAvailability] Successfully fetched and cached ${events.length} events`);
        return { ...result, items: events };
      } finally {
        // Remove from pending requests when done
        pendingRequests.delete(cacheKey);
      }
    })();

    // Store the promise for deduplication
    pendingRequests.set(cacheKey, requestPromise);
    
    return await requestPromise;

    
  } catch (error: any) {
    console.error("Error fetching host availability:", error);
    // Clean up pending request on error
    const cacheKey = generateCacheKey(hostUserId, start, end, timezone, excludeEventId);
    pendingRequests.delete(cacheKey);
    return {
      items: [],
      error: error instanceof Error ? error.message : "Unknown error fetching availability",
    };
  }
}

/**
 * Clear availability cache for a specific host or all cache
 */
export function clearAvailabilityCache(hostUserId?: string): void {
  if (hostUserId) {
    // Clear cache entries for specific host
    for (const [key] of availabilityCache.entries()) {
      if (key.includes(`host_${hostUserId}_`)) {
        availabilityCache.delete(key);
      }
    }
    console.log(`[fetchHostAvailability] Cleared cache for host ${hostUserId}`);
  } else {
    // Clear all cache
    availabilityCache.clear();
    console.log(`[fetchHostAvailability] Cleared all availability cache`);
  }
}

/**
 * Preload availability for adjacent months to improve UX
 */
export async function preloadAdjacentMonths(
  hostUserId: string,
  currentDate: Date,
  timezone?: string
): Promise<void> {
  if (hostUserId === 'debug') return;
  
  const currentMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
  
  // Preload previous month
  const prevMonth = new Date(currentMonth);
  prevMonth.setMonth(prevMonth.getMonth() - 1);
  const prevMonthEnd = new Date(prevMonth.getFullYear(), prevMonth.getMonth() + 1, 0, 23, 59, 59, 999);
  
  // Preload next month
  const nextMonth = new Date(currentMonth);
  nextMonth.setMonth(nextMonth.getMonth() + 1);
  const nextMonthEnd = new Date(nextMonth.getFullYear(), nextMonth.getMonth() + 1, 0, 23, 59, 59, 999);
  
  // Fire and forget - don't await these
  Promise.all([
    fetchHostAvailability(hostUserId, prevMonth.toISOString(), prevMonthEnd.toISOString(), timezone),
    fetchHostAvailability(hostUserId, nextMonth.toISOString(), nextMonthEnd.toISOString(), timezone)
  ]).catch(err => {
    console.warn('[fetchHostAvailability] Failed to preload adjacent months:', err);
  });
  
  console.log(`[fetchHostAvailability] Started preloading adjacent months for ${hostUserId}`);
}
